import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';

import { documentService } from '../../services/documentService';
import { getDaysUntilExpiry, getDocumentStatus, needsAttention } from '../../utils/documentExpiry';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * Badge config for expiry thresholds.
 */
const getExpiryBadge = (status, days) => {
  if (status === 'expired') {
    return {
      label: 'Expired',
      bg: 'bg-red-100',
      text: 'text-red-700',
      border: 'border-red-200',
      icon: 'XCircle',
      iconColor: '#dc2626',
      rowBorder: 'border-l-4 border-l-red-500',
    };
  }
  if (status === 'expiring-critical') {
    return {
      label: `${days}d left`,
      bg: 'bg-orange-100',
      text: 'text-orange-700',
      border: 'border-orange-200',
      icon: 'AlertTriangle',
      iconColor: '#ea580c',
      rowBorder: 'border-l-4 border-l-orange-500',
    };
  }
  // expiring-soon (≤30 days)
  return {
    label: `${days}d left`,
    bg: 'bg-amber-100',
    text: 'text-amber-700',
    border: 'border-amber-200',
    icon: 'AlertCircle',
    iconColor: '#d97706',
    rowBorder: 'border-l-4 border-l-amber-400',
  };
};

const ExpiringDocumentsCard = ({ siteId = null, maxItems = 5, className = '' }) => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t } = useLanguage();

  useEffect(() => {
    loadDocuments();
  }, [siteId]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const result = siteId
        ? await documentService?.getDocumentsBySite(siteId)
        : await documentService?.getAllDocuments();

      if (result?.error) {
        // Non-blocking widget — a failed load leaves the card empty rather
        // than breaking the page. Log the cause so it's diagnosable, but
        // don't render an error state inline (the dashboard has its own
        // error reporting; this is a courtesy summary card).
        console.warn('[ExpiringDocumentsCard] load failed (non-blocking):', {
          code: result?.error?.code,
          message: result?.error?.message,
        });
      } else {
        const docs = (result?.data || [])?.filter(doc =>
          needsAttention(doc?.expiry_date || doc?.expiryDate)
        );

        // Sort: expired first, then by days ascending
        docs?.sort((a, b) => {
          const dA = getDaysUntilExpiry(a?.expiry_date || a?.expiryDate) ?? 999;
          const dB = getDaysUntilExpiry(b?.expiry_date || b?.expiryDate) ?? 999;
          return dA - dB;
        });

        setDocuments(docs);
      }
    } catch (e) {
      // Non-blocking widget; log via console.warn so the cause is at least
      // diagnosable instead of a literal silent swallow.
      console.warn('[ExpiringDocumentsCard] load threw (non-blocking):', { message: e?.message });
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = (doc) => {
    const docId = doc?.id;
    const docSiteId = doc?.site_id;
    if (docSiteId && docId) {
      navigate(`/sites/${docSiteId}/documents/${docId}`, { state: { document: doc } });
    } else if (docId) {
      navigate(`/documents/${docId}`, { state: { document: doc } });
    }
  };

  const handleReplace = (e, doc) => {
    e?.stopPropagation();
    const docId = doc?.id;
    const docSiteId = doc?.site_id;
    if (docSiteId && docId) {
      navigate(`/sites/${docSiteId}/documents/${docId}`, { state: { document: doc, openReplace: true } });
    } else if (docId) {
      navigate(`/documents/${docId}`, { state: { document: doc, openReplace: true } });
    }
  };

  const handleUpdateDate = (e, doc) => {
    e?.stopPropagation();
    const docId = doc?.id;
    const docSiteId = doc?.site_id;
    if (docSiteId && docId) {
      navigate(`/sites/${docSiteId}/documents/${docId}`, { state: { document: doc, openEdit: true } });
    } else if (docId) {
      navigate(`/documents/${docId}`, { state: { document: doc, openEdit: true } });
    }
  };

  const expiredCount = documents?.filter(d => getDocumentStatus(d?.expiry_date || d?.expiryDate) === 'expired')?.length;
  const criticalCount = documents?.filter(d => getDocumentStatus(d?.expiry_date || d?.expiryDate) === 'expiring-critical')?.length;
  const soonCount = documents?.filter(d => getDocumentStatus(d?.expiry_date || d?.expiryDate) === 'expiring-soon')?.length;

  if (loading) {
    return (
      <div className={`bg-card rounded-xl border border-border shadow-elevation-2 p-5 ${className}`}>
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-muted rounded w-1/2" />
          <div className="h-16 bg-muted rounded" />
          <div className="h-16 bg-muted rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-card rounded-xl border border-border shadow-elevation-2 overflow-hidden ${className}`}>
      {/* Header */}
      <div className="px-5 py-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon name="FileWarning" size={18} className="text-warning" />
            <h3 className="font-semibold text-foreground font-heading">{t('documentsNeedingAttention')}</h3>
          </div>
          <button
            onClick={() => navigate('/document-management')}
            className="text-xs font-semibold text-accent hover:text-accent/80 transition-colors flex items-center gap-1"
          >
            {t('viewAll')} <Icon name="ArrowRight" size={13} />
          </button>
        </div>

        {/* Summary pills */}
        {documents?.length > 0 && (
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            {expiredCount > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-semibold border border-red-200">
                <Icon name="XCircle" size={11} color="#dc2626" />
                {expiredCount} expired
              </span>
            )}
            {criticalCount > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 text-xs font-semibold border border-orange-200">
                <Icon name="AlertTriangle" size={11} color="#ea580c" />
                {criticalCount} within 14 days
              </span>
            )}
            {soonCount > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold border border-amber-200">
                <Icon name="AlertCircle" size={11} color="#d97706" />
                {soonCount} within 30 days
              </span>
            )}
          </div>
        )}
      </div>

      {/* Document list */}
      {documents?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 px-5">
          <Icon name="CheckCircle2" size={40} className="text-success mb-3" />
          <p className="text-sm font-medium text-foreground">{t('allDocumentsUpToDate')}</p>
          <p className="text-xs text-muted-foreground mt-1">{t('noDocumentsExpiring')}</p>
        </div>
      ) : (
        <div className="divide-y divide-border">
          {documents?.slice(0, maxItems)?.map((doc) => {
            const expiryDate = doc?.expiry_date || doc?.expiryDate;
            const days = getDaysUntilExpiry(expiryDate);
            const status = getDocumentStatus(expiryDate);
            const badge = getExpiryBadge(status, days);

            return (
              <button
                key={doc?.id}
                onClick={() => handleCardClick(doc)}
                className={`w-full text-left px-5 py-4 hover:bg-muted/40 transition-colors ${badge?.rowBorder} focus:outline-none focus-visible:ring-2 focus-visible:ring-accent`}
                role="button"
                tabIndex={0}
              >
                <div className="flex items-start gap-3">
                  {/* Icon */}
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 border ${badge?.bg} ${badge?.border}`}>
                    <Icon name={badge?.icon} size={16} color={badge?.iconColor} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-medium text-foreground truncate">{doc?.title || doc?.name || 'Untitled Document'}</p>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0 border ${badge?.bg} ${badge?.text} ${badge?.border}`}>
                        {badge?.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {doc?.category && (
                        <span className="text-xs text-muted-foreground">{doc?.category}</span>
                      )}
                      {doc?.category && expiryDate && (
                        <span className="text-xs text-muted-foreground">·</span>
                      )}
                      {expiryDate && (
                        <span className="text-xs text-muted-foreground">
                          {status === 'expired' ? 'Expired' : 'Expires'}{' '}
                          {new Date(expiryDate)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                      )}
                    </div>

                    {/* Action buttons */}
                    <div className="flex items-center gap-2 mt-2" onClick={e => e?.stopPropagation()}>
                      <button
                        onClick={(e) => handleReplace(e, doc)}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-accent text-accent-foreground text-xs font-semibold hover:bg-accent/90 transition-colors"
                      >
                        <Icon name="Upload" size={11} />
                        {t('replace')}
                      </button>
                      <button
                        onClick={(e) => handleUpdateDate(e, doc)}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-border bg-background text-foreground text-xs font-semibold hover:bg-muted transition-colors"
                      >
                        <Icon name="CalendarDays" size={11} />
                        {t('updateDate')}
                      </button>
                    </div>
                  </div>

                  <Icon name="ChevronRight" size={16} className="text-muted-foreground flex-shrink-0 mt-1" />
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Footer: show more */}
      {documents?.length > maxItems && (
        <div className="px-5 py-3 bg-muted/30 border-t border-border">
          <button
            onClick={() => navigate('/document-management?status=expiring-soon')}
            className="text-sm text-accent hover:text-accent/80 font-medium flex items-center gap-1"
          >
            +{documents?.length - maxItems} more documents <Icon name="ArrowRight" size={14} />
          </button>
        </div>
      )}
    </div>
  );
};

export default ExpiringDocumentsCard;
